import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * One "something went wrong" display, used everywhere.
 *
 * There were two before - one in ErrorBoundary and one in the route error page -
 * with slightly different wording and spacing. Two implementations of the same
 * idea drift, and the user sees whichever one happened to catch their failure.
 */
export function ErrorState({
  code,
  title,
  description,
  action,
  className,
  full = false,
}: {
  /** A status or short label shown above the title, e.g. '404'. */
  code?: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
  className?: string;
  /** Fills the viewport, for a whole-page failure rather than a panel. */
  full?: boolean;
}) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-4 p-8 text-center',
        full ? 'min-h-dvh bg-background text-foreground' : 'min-h-96',
        className,
      )}
    >
      {code !== undefined && <p className="font-mono text-sm text-muted-foreground">{code}</p>}

      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
      </div>

      {action}
    </div>
  );
}
